import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccountServiceBase } from '@seahorse/auth';
import { NotificationService } from '@seahorse/common';
import { MfaSetupCode } from '@seahorse/domain';
import { Subscription } from 'rxjs';
import { LanguagePickerComponent } from '../language-picker/language-picker.component';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { MfaSetupComponent } from '../mfa-setup/mfa-setup.component';
import { MfaService } from '../mfa-setup/mfa.service';

@Component({
  selector: 'sh-activate-account',
  templateUrl: './activate-account.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LoadingIndicatorComponent,
    LanguagePickerComponent,
    MfaSetupComponent
  ]
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
  @ViewChild('mfaSetup', { static: false })
  mfaSetupComponent?: MfaSetupComponent;

  isLoading = true;
  userId = '';
  activated = false;
  mfaSetupCode?: MfaSetupCode;
  isTokenExpired = false;

  private _subscription = new Subscription();

  private _accountService = inject(AccountServiceBase);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _mfaService = inject(MfaService);
  private _notificationService = inject(NotificationService);

  ngOnInit() {
    this._subscription.add(
      this._route.queryParamMap.subscribe((params) => {
        const token = params.get('token');
        const invitation = params.get('invitation') ?? '';
        
        this.userId = token ?? '';

        const mfaSetupInProgress = localStorage.getItem('mfaSetupInProgress');
        if (mfaSetupInProgress) {
          this.isLoading = false;
          this.activated = true;
          return;
        }

        if (token) {
          this._subscription.add(
            this._accountService
              .activateAccount(token, invitation)
              .subscribe((response) => {
                if (response) {
                  this.isLoading = false;
                  this.activated = true;
                  setTimeout(() => {
                    this._accountService.activateAccountMessage();
                  }, 1000);
                } else {
                  this.isTokenExpired = true;
                }
              })
          );
        } else {
          this.isLoading = false;
          this._router.navigate(['account/login'], {queryParamsHandling: 'merge'});
        }
      })
    );
  }

  initMfa() {
    localStorage.setItem('mfaSetupInProgress', this._router.url);

    this._subscription.add(
      this._mfaService.initMfaWithUserId(this.userId).subscribe((res) => {
        this.mfaSetupCode = res.result;
      })
    );
  }

  completeMfa() {
    if (!this.mfaSetupComponent) {
      return;
    }

    const code = this.mfaSetupComponent.codeControl.getRawValue();

    if (!code?.length) {
      this._notificationService.showError('user.mfa.verificationCodeRequired');

      return;
    }

    this._subscription.add(
      this._mfaService
        .completeMfaWithUserId(this.userId, code)
        .subscribe((res) => {
          if (res.result) {
            this._notificationService.showSuccess(
              'user.mfa.enabled',
              'shared.terms.success'
            );

            localStorage.removeItem('mfaSetupInProgress');
            this._router.navigate(['/']);
          } else {
            this._notificationService.showError(
              'user.mfa.invalidVerificationCode',
              'shared.terms.error'
            );
          }
        })
    );
  }

  resendToken() {
    this.isLoading = true;
    this._accountService.resendActivationToken(this.userId).subscribe((res) => {
      if (res.result) {
        this._notificationService.showSuccess(
          'accounts.activate.resendTokenSuccess',
          'shared.terms.success'
        );
        this.backToLogin();
      } else {
        this._notificationService.showError('shared.terms.error');
      }
    });
  }

  backToLogin() {
    localStorage.removeItem('mfaSetupInProgress');
    this._router.navigate(['account/login'], {queryParamsHandling: 'merge'});
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
